<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container, FanControl, SliderV2 } from '@Shelly/shelly-design-system';
	import { setVentoraFan } from 'src/services/ventora_fan';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { devices_status } from 'src/stores/device-runtime/status';
	import { os } from 'src/stores/session/native-bridge';
	import { real_id } from 'src/helpers/interface_helpers';
	import { hapticFeedback } from 'src/services/native_container_requests_ios';
	import {
		patchFan0StatusFromFanSetResponse,
		readVentoraFanOn,
		readVentoraFanSpeedPercent,
		ventoraClamp,
	} from 'src/components/DevicesSettings/Settings/SettingsG2/VentoraFan/ventora_fan_settings_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let device;
	export let status;
	export let className = '';
	export { className as class };

	const id = real_id(device.id);
	const isNewLayout = $showNewLayout;
	let loading = false;
	let value = 0;
	let targetValue: number | undefined;
	let reqSeq = 0;

	$: fan0 = status?.['fan:0'];
	$: speedFromStore = ventoraClamp(
		readVentoraFanSpeedPercent(fan0 as Record<string, unknown> | undefined),
		1,
		100,
	);

	$: if (fan0 && fan0.mode !== 'auto') {
		const adjusting = targetValue !== undefined && speedFromStore !== targetValue;

		if (!adjusting) {
			value = readVentoraFanOn(fan0 as Record<string, unknown>) ? speedFromStore : 0;
			if (targetValue !== undefined && speedFromStore === targetValue) {
				targetValue = undefined;
			}
		}
	}

	$: sliderA11y = {
		sliderAriaLabel: $format('label_fan_speed', { default: 'Fan speed' }),
		ariaLabel: $format('label_fan_speed', { default: 'Fan speed' }),
		ariaLabelValue: $format('label_fan_speed', { default: 'Fan speed' }),
	};

	const applyFanPosition = async (position: number) => {
		const st = devices_status.getByRealId(device.id)?.['fan:0'];
		if (!status?.online || !st || st.mode === 'auto') return;
		const isOn = position >= 1;
		const clamped = ventoraClamp(position, 0, 100);
		const seq = ++reqSeq;
		loading = true;
		try {
			const fanRes = await setVentoraFan(device, {
				id: 0,
				mode: 'manual',
				turn: isOn,
				speed: clamped,
			});
			if (seq !== reqSeq) {
				return;
			}
			const fanResp = onSettingsResponse(fanRes, device, {}, false, 'Failed to set fan speed');
			if (fanResp?.isok) {
				patchFan0StatusFromFanSetResponse(device, fanResp as unknown as Record<string, unknown>);
			} else {
				targetValue = undefined;
				const sp = readVentoraFanSpeedPercent(
					devices_status.getByRealId(device.id)?.['fan:0'] as
						| Record<string, unknown>
						| undefined,
				);
				value = ventoraClamp(sp, 1, 100);
			}
		} finally {
			if (seq === reqSeq) {
				loading = false;
			}
		}
	};

	const handleValueChange = async (e: CustomEvent<{ value: number }>) => {
		const st = devices_status.getByRealId(device.id)?.['fan:0'];
		let _value = e.detail.value;
		if (!status?.online || !st || st.mode === 'auto') return;
		if (_value > 0) {
			_value = ventoraClamp(e.detail.value, 0, 100);
		}
		targetValue = _value;
		await applyFanPosition(Number(_value.toFixed(0)));
	};

	const handleMove = () => {
		if ($os === 'IOS') {
			hapticFeedback('selection');
		}
	};

	$: sliderClass = `flex-full-height justify-content-center ${className}`.trim();
</script>

{#if fan0 && fan0.mode !== 'auto'}
	{#if isNewLayout}
		<Container class="flex-full-height justify-content-center">
			<FanControl
				{value}
				type="speed"
				min={0}
				max={100}
				step={1}
				deviceName={device.name}
				displayMode="percent"
				a11yProps={{
					toggleButtonAriaLabel: 'Toggle hall fan coil',
					speedSliderAriaLabel: 'Fan speed',
					temperatureSliderAriaLabel: 'Target temperature',
				}}
				on:value_change={handleValueChange}
				on:move={handleMove}
			/>
		</Container>
	{:else}
		<SliderV2
			id="slider-ventora-fan-speed-{id}"
			a11yProps={sliderA11y}
			class={sliderClass}
			{value}
			type="vertical"
			min={0}
			max={100}
			step={1}
			unit="%"
			haveMarks={false}
			stopSaveDeleteMarks={device.shared}
			isLoading={loading}
			title={$format('ventora_manual_mode_speed', {
				values: { speed: value },
				default: 'Speed {speed}%',
			})}
			on:value_change={handleValueChange}
			on:move={handleMove}
		/>
	{/if}
{/if}
